import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

interface YearRow {
  learningArea: string;
  yearLevel: string;
  total: number;
  solid: number;
  inProgress: number;
  untouched: number;
}

interface ChildProgress {
  childId: string;
  nickname: string;
  yearLevel: string;
  rows: YearRow[];
}

/**
 * Where each child is against each year of the curriculum.
 *
 * The dashboard answers "did they turn up" - streaks, minutes, stars. It has
 * never answered "are they where they should be", which is the question a
 * parent actually has, and the one that sent us looking when a Year 1 child
 * turned out to be grinding Foundation counting.
 *
 * Deliberately plain. No score, no percentile, no label on the child: three
 * numbers per year - what they can do unaided, what they are in the middle of,
 * what they have not met - and a marker on their own year so ahead and behind
 * are visible without anyone having to say the words.
 */
const YEAR_ORDER = ['F', '1', '2', '3', '4', '5', '6'];
const YEAR_NAME: Record<string, string> = { F: 'Prep' };

/** 'Prep' and 'Year 1' both have to line up with an 'F' or a '1' on the chart. */
function rankOf(label: string | undefined): number {
  if (!label) return 0;
  const digits = /(\d+)/.exec(label);
  return digits ? Number(digits[1]) : 0;
}

export function AdminProgress() {
  const progress = useQuery({
    queryKey: ['admin', 'progress'],
    queryFn: () => api<{ children: ChildProgress[] }>('/admin/progress'),
  });

  return (
    <main className="admin">
      <p className="crumb">
        <Link to="/admin">&larr; Grown-ups</Link>
      </p>
      <h1>Progress against the curriculum</h1>
      <p className="muted">
        What each child can do unaided, what they are part-way through, and what they have not met
        yet &mdash; for every year of the curriculum, not just their own.
      </p>

      {progress.isLoading && <p>Loading&hellip;</p>}
      {progress.isError && (
        <p className="error">
          That did not load. If you have been away a while, sign in again and retry.
        </p>
      )}

      {progress.data?.children.map((child) => {
        const childRank = rankOf(child.yearLevel);
        const areas = [...new Set(child.rows.map((row) => row.learningArea))].sort();

        return (
          <section key={child.childId} className="card progress-card">
            <h2>
              {child.nickname} <span className="progress-year">{child.yearLevel}</span>
            </h2>

            {areas.map((area) => (
              <div key={area} className="progress-area">
                <h3>{area}</h3>
                {child.rows
                  .filter((row) => row.learningArea === area)
                  .sort((a, b) => YEAR_ORDER.indexOf(a.yearLevel) - YEAR_ORDER.indexOf(b.yearLevel))
                  .map((row) => {
                    const rank = rankOf(row.yearLevel === 'F' ? 'F' : row.yearLevel);
                    const relation =
                      rank === childRank ? 'own' : rank < childRank ? 'behind' : 'ahead';
                    const pct = (n: number) => `${(n / row.total) * 100}%`;

                    return (
                      <div key={row.yearLevel} className="progress-row" data-relation={relation}>
                        <span className="progress-label">
                          {YEAR_NAME[row.yearLevel] ?? `Year ${row.yearLevel}`}
                          {relation === 'own' && <em> &middot; their year</em>}
                        </span>

                        <div
                          className="progress-bar"
                          role="img"
                          aria-label={`${row.solid} of ${row.total} solid, ${row.inProgress} in progress`}
                        >
                          <span
                            className="progress-seg progress-seg--solid"
                            style={{ width: pct(row.solid) }}
                          />
                          <span
                            className="progress-seg progress-seg--doing"
                            style={{ width: pct(row.inProgress) }}
                          />
                        </div>

                        <span className="progress-count">
                          {row.solid}/{row.total}
                        </span>
                      </div>
                    );
                  })}
              </div>
            ))}

            <p className="progress-key">
              <span className="progress-dot progress-dot--solid" /> can do unaided
              <span className="progress-dot progress-dot--doing" /> part-way through
              <span className="progress-dot progress-dot--none" /> not met yet
            </p>
          </section>
        );
      })}
    </main>
  );
}
